import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { fetchArchives, type Archive } from '../api/archives';
import { importEditFromUrl } from '../api/importEdit';
import type { PreviewEngine } from '../engine/preview';
import { totalDuration } from '../format/helpers';
import { installKeymap } from '../keys/keymap';
import { useKeyBindings } from '../keys/useKeyScope';
import { jumpToKeyframe } from './inspector/keyframeNav';
import { saveToBin } from '../state/bin';
import { cacheClips, cancelCaching } from '../state/clipcache';
import { usePlayback } from '../state/playback';
import { useChat } from '../state/chat';
import { useProject, type SaveState } from '../state/project';
import { useTimeline } from '../state/timeline';
import BinPanel, { type BinDetailRequest } from './bin/BinPanel';
import ErrorBoundary from './ErrorBoundary';
import ExportDialog from './ExportDialog';
import Inspector from './inspector/Inspector';
import PreviewPlayer from './PreviewPlayer';
import ChatPanel from './chat/ChatPanel';
import AvatarStack from './collab/AvatarStack';
import CollabButton from './CollabButton';
import PublishButton from './PublishButton';
import AdvancedSearchOverlay from './search/AdvancedSearchOverlay';
import RailResizer, { useRailWidth } from './RailResizer';
import SearchPanel from './SearchPanel';
import ShortcutsHelp from './ShortcutsHelp';
import Timeline from './Timeline';
import './EditorPage.css';

export interface PlayerControls {
  toggle(): void;
  pause(): void;
  seek(t: number): void;
  stepFrame(dir: 1 | -1, big?: boolean): void;
  /** J/K/L shuttle: play in `dir`, cycling 1→2→4 on repeated same-direction calls. */
  shuttle(dir: 1 | -1): void;
}

const SAVE_LABEL: Record<SaveState, string> = {
  saved: 'Saved',
  dirty: 'Unsaved changes',
  saving: 'Saving…',
  error: 'Save failed — retrying on next change',
  offline: 'Offline',
};

export default function EditorPage({ collab = false }: { collab?: boolean } = {}) {
  const { id, token } = useParams<{ id: string; token: string }>();
  const engineRef = useRef<PreviewEngine | null>(null);
  const [archives, setArchives] = useState<Archive[] | null>(null);
  // Key bindings register once on mount (useKeyBindings defaults deps to []),
  // but archives load asynchronously — a captured `archives` would be null
  // forever. Read it through a ref inside run() instead.
  const archivesRef = useRef<Archive[] | null>(null);
  archivesRef.current = archives;
  const [searchOpen, setSearchOpen] = useState(true);
  const [railTab, setRailTab] = useState<'search' | 'bin' | 'chat'>('search');
  // Widening the rail is how the assistant conversation gets room without
  // hiding the timeline behind a modal — see RailResizer's header.
  const [railWidth, setRailWidth] = useRailWidth();
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [binDetail, setBinDetail] = useState<BinDetailRequest | null>(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const importDoneRef = useRef(false);

  const project = useProject((s) => s.project);
  const loadError = useProject((s) => s.loadError);
  const saveState = useProject((s) => s.saveState);
  const loadProject = useProject((s) => s.load);
  const loadCollab = useProject((s) => s.loadCollab);
  const unloadProject = useProject((s) => s.unload);
  const setTitle = useProject((s) => s.setTitle);
  const connection = useProject((s) => s.connection);

  const unreadChat = useChat((s) => s.unread);
  const setChatOpen = useChat((s) => s.setOpen);

  // Opening the tab clears the unread badge; collapsing the rail hides chat too.
  useEffect(() => {
    setChatOpen(searchOpen && railTab === 'chat');
  }, [searchOpen, railTab, setChatOpen]);

  const canUndo = useTimeline((s) => s.undoDepth > 0);
  const canRedo = useTimeline((s) => s.redoDepth > 0);

  useEffect(() => {
    void fetchArchives().then(setArchives);
  }, []);

  useEffect(() => {
    if (collab && token) void loadCollab(token);
    else if (id) void loadProject(id);
    return () => unloadProject();
  }, [collab, token, id, loadProject, loadCollab, unloadProject]);

  // Seed a freshly-created project from an edit URL handed over by ProjectsPage
  // (navigate state). Runs once, after the project timeline + archives load.
  const importEditUrl = (location.state as { importEditUrl?: string } | null)?.importEditUrl;
  useEffect(() => {
    if (!importEditUrl || importDoneRef.current) return;
    if (!archives || !project || String(project.id) !== id) return;
    importDoneRef.current = true;
    navigate(location.pathname, { replace: true, state: null });
    void (async () => {
      try {
        const result = await importEditFromUrl(importEditUrl, archives);
        if (result.clips.length === 0) {
          setImportError('That edit had no importable clips.');
          return;
        }
        const videoTrack = useTimeline.getState().edit.tracks.find((t) => t.kind === 'video');
        useTimeline.getState().insertEditClips(result.clips, 0, videoTrack?.id);
      } catch (e) {
        setImportError(e instanceof Error ? e.message : 'Import failed.');
      }
    })();
  }, [importEditUrl, archives, project, id, navigate, location.pathname]);

  const shuttleRef = useRef(1); // current signed shuttle rate (engine resets to 1 on pause)

  // Stable identity: everything inside reads refs or store state at call time,
  // and Timeline's context (which feeds every memoized clip) depends on it.
  const controls: PlayerControls = useMemo((): PlayerControls => ({
    toggle() {
      const e = engineRef.current;
      if (!e) return;
      shuttleRef.current = 1; // Space always plays/pauses at normal speed
      if (e.isPlaying) {
        e.pause();
        usePlayback.getState().setPlaying(false);
      } else {
        e.setRate(1);
        e.play();
        usePlayback.getState().setPlaying(true);
      }
    },
    pause() {
      shuttleRef.current = 1;
      engineRef.current?.pause();
      usePlayback.getState().setPlaying(false);
    },
    shuttle(dir) {
      const e = engineRef.current;
      if (!e) return;
      const prev = shuttleRef.current;
      // repeated same-direction presses while playing cycle 1→2→4→1
      const cycling = e.isPlaying && Math.sign(prev) === dir;
      const mag = cycling ? (Math.abs(prev) >= 4 ? 1 : Math.abs(prev) * 2) : 1;
      shuttleRef.current = dir * mag;
      e.setRate(dir * mag);
      if (!e.isPlaying) {
        e.play();
        usePlayback.getState().setPlaying(true);
      }
    },
    seek(t: number) {
      const dur = totalDuration(useTimeline.getState().edit);
      const clamped = Math.max(0, Math.min(t, dur));
      engineRef.current?.seek(clamped);
      usePlayback.getState().setTime(clamped);
    },
    stepFrame(dir, big = false) {
      const fps = useTimeline.getState().edit.settings.fps;
      const step = big ? 1 : 1 / fps;
      this.seek(usePlayback.getState().currentTime + dir * step);
    },
  }), []);
  const controlsRef = useRef(controls);
  controlsRef.current = controls;

  useEffect(() => installKeymap(), []);

  useKeyBindings(() => {
    const c = () => controlsRef.current;
    const tl = () => useTimeline.getState();
    return [
      {
        id: 'transport.playPause',
        keys: ['Space'],
        scope: 'global',
        group: 'Transport',
        description: 'Play / pause',
        run: () => c().toggle(),
      },
      {
        id: 'transport.shuttleReverse',
        keys: ['J'],
        scope: 'global',
        group: 'Transport',
        description: 'Shuttle reverse (J again: faster)',
        run: () => c().shuttle(-1),
      },
      {
        id: 'transport.shuttleStop',
        keys: ['K'],
        scope: 'global',
        group: 'Transport',
        description: 'Pause (resets shuttle speed)',
        run: () => c().pause(),
      },
      {
        id: 'transport.shuttleForward',
        keys: ['L'],
        scope: 'global',
        group: 'Transport',
        description: 'Shuttle forward (L again: faster)',
        run: () => c().shuttle(1),
      },
      {
        id: 'transport.loopIn',
        keys: ['I'],
        scope: 'global',
        group: 'Transport',
        description: 'Loop in point at playhead',
        run: () => {
          const p = usePlayback.getState();
          p.setLoopIn(p.currentTime);
        },
      },
      {
        id: 'transport.loopOut',
        keys: ['O'],
        scope: 'global',
        group: 'Transport',
        description: 'Loop out point at playhead',
        run: () => {
          const p = usePlayback.getState();
          p.setLoopOut(p.currentTime);
        },
      },
      {
        id: 'transport.loopClear',
        keys: ['Alt+I', 'Alt+O'],
        scope: 'global',
        group: 'Transport',
        description: 'Clear loop region',
        run: () => usePlayback.getState().clearLoop(),
      },
      {
        id: 'transport.stepBack',
        keys: ['ArrowLeft'],
        scope: 'global',
        group: 'Transport',
        description: 'Step back one frame',
        run: () => c().stepFrame(-1),
      },
      {
        id: 'transport.stepForward',
        keys: ['ArrowRight'],
        scope: 'global',
        group: 'Transport',
        description: 'Step forward one frame',
        run: () => c().stepFrame(1),
      },
      {
        id: 'transport.jumpBack',
        keys: ['Shift+ArrowLeft'],
        scope: 'global',
        group: 'Transport',
        description: 'Jump back one second',
        run: () => c().stepFrame(-1, true),
      },
      {
        id: 'transport.jumpForward',
        keys: ['Shift+ArrowRight'],
        scope: 'global',
        group: 'Transport',
        description: 'Jump forward one second',
        run: () => c().stepFrame(1, true),
      },
      {
        id: 'transport.goToStart',
        keys: ['Home'],
        scope: 'global',
        group: 'Transport',
        description: 'Go to start',
        run: () => c().seek(0),
      },
      {
        id: 'transport.goToEnd',
        keys: ['End'],
        scope: 'global',
        group: 'Transport',
        description: 'Go to end',
        run: () => c().seek(totalDuration(tl().edit)),
      },
      {
        id: 'edit.undo',
        keys: ['Mod+Z'],
        scope: 'global',
        group: 'Editing',
        description: 'Undo',
        run: () => tl().undo(),
      },
      {
        id: 'edit.redo',
        keys: ['Mod+Shift+Z', 'Mod+Y'],
        scope: 'global',
        group: 'Editing',
        description: 'Redo',
        run: () => tl().redo(),
      },
      {
        id: 'edit.delete',
        keys: ['Delete', 'Backspace'],
        scope: 'global',
        group: 'Editing',
        description: 'Delete selected clips (leaves a gap)',
        run: () => {
          const s = tl();
          if (s.selection.length) s.deleteClips(s.selection);
        },
      },
      {
        id: 'edit.rippleDelete',
        keys: ['Shift+Delete', 'Shift+Backspace'],
        scope: 'global',
        group: 'Editing',
        description: 'Ripple delete (closes the gap)',
        run: () => {
          const s = tl();
          if (s.selection.length) s.rippleDelete(s.selection);
        },
      },
      {
        id: 'edit.cache',
        // R for "Render": this is the network-fetch analogue of the render step
        // in older editors. Modified rather than bare — it starts a download
        // that can run for minutes.
        keys: ['Shift+R'],
        scope: 'global',
        group: 'Editing',
        description: 'Cache selected clips locally — like Render in older editors',
        run: () => {
          const s = tl();
          const a = archivesRef.current;
          if (a && s.selection.length) cacheClips(s.selection, s.edit, a);
        },
      },
      {
        id: 'edit.cacheCancel',
        keys: ['Alt+R'],
        scope: 'global',
        group: 'Editing',
        description: 'Cancel caching',
        run: () => {
          const a = archivesRef.current;
          if (a) cancelCaching(tl().edit, a);
        },
      },
      {
        id: 'edit.copy',
        keys: ['Mod+C'],
        scope: 'global',
        yieldToTextSelection: true,
        group: 'Editing',
        description: 'Copy selected clips',
        run: () => {
          const s = tl();
          if (s.selection.length) s.copyClips(s.selection);
        },
      },
      {
        id: 'edit.cut',
        keys: ['Mod+X'],
        scope: 'global',
        yieldToTextSelection: true,
        group: 'Editing',
        description: 'Cut selected clips',
        run: () => {
          const s = tl();
          if (s.selection.length) s.cutClips(s.selection);
        },
      },
      {
        id: 'edit.paste',
        keys: ['Mod+V'],
        scope: 'global',
        group: 'Editing',
        description: 'Paste clips at playhead',
        run: () => tl().pasteClips(usePlayback.getState().currentTime),
      },
      {
        id: 'edit.split',
        keys: ['S'],
        scope: 'global',
        group: 'Editing',
        description: 'Split selected clips at playhead',
        run: () => {
          const s = tl();
          if (s.selection.length) s.splitAt(usePlayback.getState().currentTime, s.selection);
        },
      },
      {
        id: 'edit.detachAudio',
        keys: ['Shift+S'],
        scope: 'global',
        group: 'Editing',
        description: "Detach selected clips' audio to its own track",
        run: () => {
          const s = tl();
          if (s.selection.length) s.detachAudio(s.selection);
        },
      },
      {
        id: 'keyframes.prev',
        keys: ['['],
        scope: 'global',
        group: 'Keyframes',
        description: 'Go to previous keyframe of selected clip',
        run: () => jumpToKeyframe((t) => c().seek(t), -1),
      },
      {
        id: 'keyframes.next',
        keys: [']'],
        scope: 'global',
        group: 'Keyframes',
        description: 'Go to next keyframe of selected clip',
        run: () => jumpToKeyframe((t) => c().seek(t), 1),
      },
      {
        id: 'help.toggle',
        keys: ['?'],
        scope: 'global',
        group: 'Help',
        description: 'Show keyboard shortcuts',
        run: () => setHelpOpen((v) => !v),
      },
    ];
  });

  if (loadError) {
    return (
      <div className="editor-error">
        <p>{loadError}</p>
        <Link to="/">← back to projects</Link>
      </div>
    );
  }

  // The timeline lives in the sync room and there is no JSON autosave to fall
  // back on, so the editor must not be usable before the document has arrived:
  // an unsynced doc is empty, looks exactly like an empty project, and any edit
  // made against it would go nowhere. Gate on the connection instead.
  if (connection === 'connecting') {
    return <div className="app-loading">connecting to the timeline…</div>;
  }
  if (connection === 'failed') {
    return (
      <div className="editor-error">
        <p>
          Could not connect to the sync service, so this timeline cannot be opened.
          {collab ? ' The collaboration link may have been revoked.' : ''}
        </p>
        <button className="primary" onClick={() => window.location.reload()}>
          Try again
        </button>
        {!collab && <Link to="/">← back to projects</Link>}
      </div>
    );
  }

  return (
    <div className="editor-page">
      <header className="editor-topbar">
        {collab ? (
          <span className="editor-home">◂ fabled</span>
        ) : (
          <Link to="/" className="editor-home" title="Back to projects">
            ◂ fabled
          </Link>
        )}
        <input
          className="editor-title"
          value={project?.title ?? ''}
          placeholder="Untitled"
          readOnly={collab}
          onChange={(e) => setTitle(e.target.value)}
        />
        {/* Once synced, the timeline's durability is the room's business, not
            the autosave's — so show the connection, and fall back to the
            title-autosave state only while the socket is healthy. */}
        {connection === 'reconnecting' ? (
          <span className="editor-savestate savestate-error">Reconnecting…</span>
        ) : (
          <span className={`editor-savestate savestate-${saveState}`}>{SAVE_LABEL[saveState]}</span>
        )}
        <ErrorBoundary label="Collaborators">
          <AvatarStack />
        </ErrorBoundary>
        <div className="editor-topbar-spacer" />
        <button
          className="icon"
          title="Undo (⌘Z)"
          disabled={!canUndo}
          onClick={() => useTimeline.getState().undo()}
        >
          ↩
        </button>
        <button
          className="icon"
          title="Redo (⇧⌘Z)"
          disabled={!canRedo}
          onClick={() => useTimeline.getState().redo()}
        >
          ↪
        </button>
        <a
          className="editor-home editor-guide"
          href="/guide"
          target="_blank"
          rel="noopener"
          title="User Guide"
        >
          ? Guide
        </a>
        {!collab && <CollabButton />}
        {!collab && <PublishButton />}
        <button className="primary" onClick={() => setExportOpen(true)}>
          Export
        </button>
      </header>

      {importError && (
        <div className="editor-import-error" onClick={() => setImportError(null)}>
          {importError} <span>(click to dismiss)</span>
        </div>
      )}

      <div className="editor-main">
        <div
          className={`editor-search ${searchOpen ? '' : 'collapsed'}`}
          style={searchOpen ? { width: railWidth, minWidth: railWidth } : undefined}
        >
          {searchOpen && (
            <div className="rail-tabs">
              <button
                className={railTab === 'search' ? 'active' : ''}
                onClick={() => setRailTab('search')}
              >
                Search
              </button>
              <button
                className={railTab === 'bin' ? 'active' : ''}
                onClick={() => setRailTab('bin')}
              >
                Bin
              </button>
              <button
                className={railTab === 'chat' ? 'active' : ''}
                onClick={() => setRailTab('chat')}
                // Without this the badge digit runs into the label and the
                // button announces as "Chat1".
                aria-label={unreadChat > 0 ? `Chat, ${unreadChat} unread` : 'Chat'}
              >
                Chat
                {unreadChat > 0 && railTab !== 'chat' && (
                  <span className="tab-badge" aria-hidden="true">
                    {unreadChat}
                  </span>
                )}
              </button>
            </div>
          )}
          {/* Chat is rendered outside the `archives` guard: it has nothing to do
              with the archive registry, and gating it there would leave the tab
              blank whenever archives are slow or unreachable. */}
          {searchOpen && railTab === 'chat' && (
            <ErrorBoundary label="Chat">
              <ChatPanel
                {...(archives ? { archives } : {})}
                onPreviewClip={(archiveKey, itemId, preset) => {
                  // Same route the advanced search overlay uses: the bin tab's
                  // detail view is the app's one item player, so previewing an
                  // assistant clip reuses it rather than growing a second one.
                  setRailTab('bin');
                  setBinDetail({ archiveKey, itemId, preset });
                }}
              />
            </ErrorBoundary>
          )}
          {searchOpen && railTab !== 'chat' && archives && (
            <ErrorBoundary label="Search">
              {railTab === 'search' ? (
                <SearchPanel archives={archives} onOpenAdvanced={() => setAdvancedOpen(true)} />
              ) : (
                <BinPanel archives={archives} detailRequest={binDetail} />
              )}
            </ErrorBoundary>
          )}
          <button
            className="editor-search-toggle ghost icon"
            title={searchOpen ? 'Collapse search' : 'Open search'}
            onClick={() => setSearchOpen((v) => !v)}
          >
            {searchOpen ? '◂' : '▸'}
          </button>
          {searchOpen && <RailResizer width={railWidth} onResize={setRailWidth} />}
        </div>
        <div className="editor-center">
          {archives && (
            <ErrorBoundary label="Preview">
              <PreviewPlayer archives={archives} engineRef={engineRef} controls={controls} />
            </ErrorBoundary>
          )}
        </div>
        <div className="editor-inspector">
          <ErrorBoundary label="Inspector">
            <Inspector archives={archives ?? []} onSeek={(t) => controlsRef.current.seek(t)} />
          </ErrorBoundary>
        </div>
      </div>

      <div className="editor-timeline">
        {archives && (
          <ErrorBoundary label="Timeline">
            <Timeline archives={archives} controls={controls} />
          </ErrorBoundary>
        )}
      </div>

      {exportOpen && archives && project && (
        <ExportDialog
          archives={archives}
          projectId={project.id}
          engineRef={engineRef}
          onClose={() => setExportOpen(false)}
        />
      )}
      {helpOpen && <ShortcutsHelp onClose={() => setHelpOpen(false)} />}
      {advancedOpen && archives && (
        <AdvancedSearchOverlay
          archives={archives}
          onAddToBin={(entry) => saveToBin(entry)}
          onOpenItem={(archiveKey, itemId, preset) => {
            setAdvancedOpen(false);
            setSearchOpen(true);
            setRailTab('bin');
            setBinDetail({ archiveKey, itemId, preset });
          }}
          onClose={() => setAdvancedOpen(false)}
        />
      )}
    </div>
  );
}
